/*
  Checkout drawer (snippets/custom-checkout-drawer.liquid). Links marked
  data-checkout-drawer open in an iframe inside a drawer instead of leaving
  the site. A new-tab link is always available as a fallback.
*/
(() => {
  if (customElements.get('checkout-drawer')) return;

  const SLOW_AFTER_MS = 10000;

  class CheckoutDrawer extends HTMLElement {
    connectedCallback() {
      // One drawer per page: when several sections render one, extra copies remove themselves
      if (window.__checkoutDrawer && window.__checkoutDrawer !== this && window.__checkoutDrawer.isConnected) {
        this.remove();
        return;
      }
      window.__checkoutDrawer = this;

      // Live at the end of <body> so no transformed ancestor can affect fixed positioning.
      // Moving the element re-runs connectedCallback, so set up only once.
      if (this.parentElement !== document.body) {
        document.body.appendChild(this);
        return;
      }
      if (this.ready) return;
      this.ready = true;

      this.panel = this.querySelector('.checkout-drawer__panel');
      this.body = this.querySelector('.checkout-drawer__body');
      this.titleEl = this.querySelector('.checkout-drawer__title');
      this.priceEl = this.querySelector('.checkout-drawer__price');
      this.status = this.querySelector('.checkout-drawer__status');
      this.slow = this.querySelector('.checkout-drawer__slow');
      this.newTab = this.querySelector('.checkout-drawer__new-tab');

      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      document.addEventListener('click', this.onDocumentClick);
      this.querySelectorAll('[data-checkout-close]').forEach((el) => el.addEventListener('click', () => this.close()));
      this.newTab.addEventListener('click', () => this.close());
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('keydown', this.onKeydown);
    }

    onDocumentClick(event) {
      const link = event.target.closest('a[data-checkout-drawer][href]');
      if (!link || event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey) return;

      const url = new URL(link.href, window.location.href);
      if (!/^https?:$/.test(url.protocol) || url.origin === window.location.origin) return;

      event.preventDefault();
      this.open({
        url: url.href,
        title: link.dataset.checkoutTitle || link.textContent.trim(),
        price: link.dataset.checkoutPrice || '',
        trigger: link,
      });
    }

    open({ url, title, price, trigger }) {
      this.trigger = trigger;
      this.titleEl.textContent = title;
      this.priceEl.textContent = price;
      this.priceEl.hidden = !price;
      this.newTab.href = url;

      this.frame?.remove();
      this.status.hidden = false;
      this.slow.hidden = true;
      clearTimeout(this.slowTimer);

      this.frame = document.createElement('iframe');
      this.frame.className = 'checkout-drawer__frame';
      this.frame.title = title;
      this.frame.src = url;
      this.frame.allow = 'payment *';
      this.frame.addEventListener('load', () => {
        this.status.hidden = true;
        clearTimeout(this.slowTimer);
      });
      this.body.appendChild(this.frame);
      this.slowTimer = setTimeout(() => (this.slow.hidden = false), SLOW_AFTER_MS);

      this.hidden = false;
      document.documentElement.classList.add('checkout-drawer-open');
      requestAnimationFrame(() => requestAnimationFrame(() => this.classList.add('is-open')));
      document.addEventListener('keydown', this.onKeydown);
      this.querySelector('.checkout-drawer__close').focus();
    }

    close() {
      if (this.hidden) return;
      this.classList.remove('is-open');
      document.removeEventListener('keydown', this.onKeydown);
      document.documentElement.classList.remove('checkout-drawer-open');
      clearTimeout(this.slowTimer);

      const finish = () => {
        this.hidden = true;
        this.frame?.remove();
        this.frame = null;
      };
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) finish();
      else setTimeout(finish, 300);

      this.trigger?.focus();
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      // Keep keyboard focus inside the drawer
      if (event.key !== 'Tab') return;
      const focusables = [this.querySelector('.checkout-drawer__close'), this.frame, this.newTab].filter(Boolean);
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  customElements.define('checkout-drawer', CheckoutDrawer);
})();
